import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { ExpandMore, Info } from "@mui/icons-material";
import { BrandButton } from "../button";
import { acme } from "../../theme/acmeTheme";
import { CircularIndicator } from "../RatingIndicators";
import DnsPropagationCheck from "../DnsPropagationCheck";
import { MAIN_DOMAIN } from "../../utils/seo";
import DnsCopyField from "./DnsCopyField";
import { toDnsRelativeHost } from "./domainsUtils";

const DomainDetailsDialog = ({
  open,
  onClose,
  selectedDomain,
  refreshingRatings,
  onRefreshDomainRatings,
  copyToClipboard,
  onOpenInfoDialog,
}) => {
  const [dnsInfoExpanded, setDnsInfoExpanded] = useState(false);
  const [bimiInfoExpanded, setBimiInfoExpanded] = useState(false);
  const [dnsPropagationExpanded, setDnsPropagationExpanded] = useState(false);

  if (!selectedDomain) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>Domain Details</span>
          <Box display="flex" alignItems="center" gap={2}>
            <CircularIndicator
              value={selectedDomain.domainRating || 0}
              label="Trust"
              size={60}
              onRefresh={() => onRefreshDomainRatings(selectedDomain._id)}
              loading={refreshingRatings[selectedDomain._id]}
            />
            <CircularIndicator
              value={selectedDomain.securityScore || 0}
              label="Security"
              size={60}
              onRefresh={() => onRefreshDomainRatings(selectedDomain._id)}
              loading={refreshingRatings[selectedDomain._id]}
            />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers style={{ whiteSpace: "pre-wrap" }}>
        <DialogContentText style={{ marginBottom: "16px" }}>
          <strong>Domain:</strong> {selectedDomain.domain}
        </DialogContentText>

        <Box sx={{ mb: 3 }}>
          <DnsPropagationCheck
            domain={selectedDomain.domain}
            onStatusChange={(status) => {
              console.log("DNS Propagation status updated:", status);
              if (status && status.propagationScore >= 100) {
                setDnsPropagationExpanded(false);
              }
            }}
            isCollapsible
            isExpanded={dnsPropagationExpanded}
            onToggleExpanded={() =>
              setDnsPropagationExpanded(!dnsPropagationExpanded)
            }
          />
        </Box>

        <Accordion
          expanded={dnsInfoExpanded}
          onChange={() => setDnsInfoExpanded(!dnsInfoExpanded)}
          sx={{ mb: 3, border: "1px solid #2196f3" }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{ backgroundColor: "#e3f2fd" }}
          >
            <Box display="flex" alignItems="center">
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", color: "#1976d2" }}
              >
                💡 DNS Cache & Troubleshooting
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: "#f8f9fa" }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              If you recently deleted and re-added this domain, or if DNS
              records show as invalid despite being correctly configured:
            </Typography>
            <Typography variant="body2" component="div">
              • <strong>DNS caching</strong> may be causing issues with stale
              data
              <br />•{" "}
              <strong>Try the &quot;Clear DNS Cache&quot; button</strong> above
              (⭮ icon)
              <br />• <strong>Wait 5-15 minutes</strong> after adding DNS
              records before checking
              <br />• <strong>Multiple checks</strong> may be needed as DNS
              propagation takes time
              <br />• <strong>Check your DNS provider</strong> to ensure records
              are properly saved
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 1, fontStyle: "italic", color: "#1565c0" }}
            >
              DNS propagation can take up to 24-48 hours globally, but typically
              completes within 1-4 hours.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="h6">SPF Setup Instructions</Typography>
          <Tooltip title="Detailed setup instructions">
            <IconButton
              onClick={() => onOpenInfoDialog("SPF")}
              sx={{ color: acme.colors.brand }}
            >
              <Info />
            </IconButton>
          </Tooltip>
        </Box>

        {selectedDomain.domain === MAIN_DOMAIN && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              border: "1px solid #2196f3",
              borderRadius: 1,
              backgroundColor: "#e3f2fd",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", color: "#1976d2", mb: 1 }}
            >
              🏠 Main Domain Configuration
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This is the system&apos;s main domain. Outbound mail is sent
              through Amazon SES, so the SPF record authorizes SES in addition
              to the mailbox provider we use for inbound mail.
            </Typography>
            <Typography variant="body2" component="div">
              • <strong>Amazon SES:</strong> <code>include:amazonses.com</code>{" "}
              authorizes SES to send on behalf of <strong>{MAIN_DOMAIN}</strong>
              <br />• <strong>Hostinger mailbox:</strong>{" "}
              <code>include:_spf.mail.hostinger.com</code>
              covers replies/inbound routing
              <br />• <strong>No self-include:</strong> we never list &quot;
              {MAIN_DOMAIN}&quot; inside its own SPF record (would be a circular
              reference)
            </Typography>
          </Box>
        )}

        {selectedDomain.spfRecord ? (
          <>
            <Typography variant="body2" gutterBottom>
              {selectedDomain.domain === MAIN_DOMAIN
                ? "Add this TXT record to your DNS zone (includes server IP directly):"
                : "Authorize outbound mail from your domain — add this TXT record:"}
            </Typography>
            <DnsCopyField
              label="Type"
              value="TXT"
              onCopy={copyToClipboard}
            />
            <DnsCopyField
              label="Name / Host"
              value="@"
              hint={`Root of ${selectedDomain.domain}. Some panels show this as blank or ${selectedDomain.domain}.`}
              copyTooltip="Copy host name"
              onCopy={copyToClipboard}
            />
            <DnsCopyField
              label="Value / Content"
              value={selectedDomain.spfRecord}
              copyTooltip="Copy SPF record"
              onCopy={copyToClipboard}
            />
          </>
        ) : (
          <Typography variant="body2" gutterBottom>
            No SPF record generated yet.
          </Typography>
        )}

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="h6">DKIM Verification (3 CNAME records)</Typography>
          <Tooltip title="Detailed setup instructions">
            <IconButton
              onClick={() => onOpenInfoDialog("DKIM")}
              sx={{ color: acme.colors.brand }}
            >
              <Info />
            </IconButton>
          </Tooltip>
        </Box>

        {(() => {
          const dkimCnames = Array.isArray(selectedDomain.dkimCnames)
            ? selectedDomain.dkimCnames
            : [];

          if (dkimCnames.length === 0) {
            return (
              <Box
                sx={{
                  p: 2,
                  mb: 3,
                  border: "1px solid #f44336",
                  borderRadius: 1,
                  backgroundColor: "#ffebee",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", color: "#d32f2f", mb: 1 }}
                >
                  🚨 SES DKIM CNAMEs not provisioned
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  We couldn&apos;t fetch the three DKIM verification CNAMEs from
                  Amazon SES for this domain. Outbound mail won&apos;t be
                  DKIM-signed until SES provisions them.
                </Typography>
                <Typography variant="body2" component="div">
                  • Delete and re-add the domain so the backend calls
                  <code> CreateEmailIdentity</code> again, or
                  <br />• Have an admin check <code>AWS_ACCESS_KEY_ID</code> /
                  <code> AWS_SECRET_ACCESS_KEY</code> / <code>AWS_REGION</code>{" "}
                  — without them the backend cannot auto-provision SES
                  identities.
                </Typography>
              </Box>
            );
          }

          return (
            <>
              {!selectedDomain.dkimStatus && (
                <Box
                  sx={{
                    p: 2,
                    mb: 3,
                    border: "1px solid #f44336",
                    borderRadius: 1,
                    backgroundColor: "#ffebee",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", color: "#d32f2f", mb: 1 }}
                  >
                    🚨 DKIM CNAMEs not found in DNS yet
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Add the three CNAME records below to your DNS. SES uses them
                    to verify domain ownership and enable automatic DKIM signing
                    — you do not create a DKIM TXT record.
                  </Typography>
                  <Typography variant="body2" component="div">
                    • Record type: <strong>CNAME</strong> (not TXT)
                    <br />
                    • In the Host/Name field, paste the relative name (without{" "}
                    <strong>{selectedDomain.domain}</strong>) — most DNS panels
                    append the domain automatically
                    <br />• Propagation typically takes 5–30 minutes
                  </Typography>
                </Box>
              )}

              <Typography variant="body2" gutterBottom>
                <strong>Amazon SES Easy DKIM</strong> — signing is automatic
                after verification. Publish these three <strong>CNAME</strong>{" "}
                records so SES can verify your domain (no DKIM TXT record
                needed).
              </Typography>

              {dkimCnames.map((cname, idx) => {
                const relativeHost = toDnsRelativeHost(
                  cname.host,
                  selectedDomain.domain,
                );
                const showFqdnHint =
                  relativeHost !== cname.host && Boolean(cname.host);

                return (
                <Box
                  key={cname.host || idx}
                  sx={{
                    p: 1.5,
                    mt: idx === 0 ? 1 : 2,
                    mb: idx === dkimCnames.length - 1 ? 2 : 0,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: acme.colors.brand, fontWeight: "bold" }}
                  >
                    CNAME #{idx + 1}
                  </Typography>
                  <DnsCopyField
                    label="Type"
                    value="CNAME"
                    onCopy={copyToClipboard}
                  />
                  <DnsCopyField
                    label="Name / Host"
                    value={relativeHost}
                    copyValue={relativeHost}
                    hint={
                      showFqdnHint
                        ? `Full DNS name: ${cname.host} — use only “${relativeHost}” if your panel adds .${selectedDomain.domain} automatically.`
                        : `Paste into the Host/Name field. If your panel appends the domain, do not add .${selectedDomain.domain} again.`
                    }
                    copyTooltip="Copy host name (without domain)"
                    onCopy={copyToClipboard}
                  />
                  <DnsCopyField
                    label="Points to / Target"
                    value={cname.target}
                    hint="Paste into Value, Target, or Points to. Trailing dots are added by most DNS panels automatically."
                    copyTooltip="Copy CNAME target"
                    onCopy={copyToClipboard}
                  />
                </Box>
              );
              })}
            </>
          );
        })()}

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="h6">DMARC Setup Instructions</Typography>
          <Tooltip title="Detailed setup instructions">
            <IconButton
              onClick={() => onOpenInfoDialog("DMARC")}
              sx={{ color: acme.colors.brand }}
            >
              <Info />
            </IconButton>
          </Tooltip>
        </Box>
        {selectedDomain.dmarcRecord ? (
          <>
            <Typography variant="body2" gutterBottom>
              Protect against spoofing — add this TXT record for DMARC policy:
            </Typography>
            <DnsCopyField
              label="Type"
              value="TXT"
              onCopy={copyToClipboard}
            />
            <DnsCopyField
              label="Name / Host"
              value="_dmarc"
              hint={`Creates _dmarc.${selectedDomain.domain}. Do not include the domain in the Host field if your panel adds it automatically.`}
              copyTooltip="Copy host name"
              onCopy={copyToClipboard}
            />
            <DnsCopyField
              label="Value / Content"
              value={selectedDomain.dmarcRecord}
              copyTooltip="Copy DMARC record"
              onCopy={copyToClipboard}
            />
          </>
        ) : (
          <Typography variant="body2" gutterBottom>
            No DMARC record generated yet.
          </Typography>
        )}

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="h6">BIMI Setup Instructions</Typography>
          <Tooltip title="Detailed setup instructions">
            <IconButton
              onClick={() => onOpenInfoDialog("BIMI")}
              sx={{ color: acme.colors.brand }}
            >
              <Info />
            </IconButton>
          </Tooltip>
        </Box>

        <Accordion
          expanded={bimiInfoExpanded}
          onChange={() => setBimiInfoExpanded(!bimiInfoExpanded)}
          sx={{ mb: 3, border: "1px solid #ff9800" }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{ backgroundColor: "#fff3e0" }}
          >
            <Box display="flex" alignItems="center">
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", color: "#f57c00" }}
              >
                ⚠️ Important: Gmail BIMI Requirements
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: "#fafafa" }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Gmail does NOT support simple SVG files for BIMI.</strong>{" "}
              To display logos in Gmail, you need:
            </Typography>
            <Typography variant="body2" component="div">
              • <strong>VMC (Verified Mark Certificate)</strong> or CMC from a
              Certificate Authority
              <br />• <strong>Registered trademark</strong> for your logo (6-12
              months process)
              <br />• <strong>PEM file</strong> instead of SVG file
              <br />• <strong>DMARC policy</strong> set to p=quarantine or
              p=reject (✓ configured)
              <br />• <strong>pct=100</strong> in DMARC policy (✓ configured)
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
              Current system generates basic BIMI records that work in some
              email clients but NOT in Gmail.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {selectedDomain.domainEmails &&
        selectedDomain.domainEmails.length > 0 ? (
          <>
            <Typography variant="body2" gutterBottom>
              Current BIMI records (basic implementation - limited email client
              support):
            </Typography>

            {selectedDomain.domainEmails.map((email, index) =>
              email.bimiRecord ? (
                <Box key={email._id} mt={2} mb={2}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: "bold", color: "#1976d2" }}
                  >
                    BIMI Record for {email.email}:
                  </Typography>
                  <DnsCopyField
                    label="Type"
                    value="TXT"
                    onCopy={copyToClipboard}
                  />
                  <DnsCopyField
                    label="Value / Content"
                    value={email.bimiRecord}
                    copyTooltip={`Copy BIMI record for ${email.email}`}
                    onCopy={copyToClipboard}
                  />
                  {index === 0 && (
                    <DnsCopyField
                      label="Name / Host"
                      value="default._bimi"
                      hint={`Creates default._bimi.${selectedDomain.domain}. Paste only default._bimi if your DNS panel appends the domain.`}
                      copyTooltip="Copy host name"
                      onCopy={copyToClipboard}
                    />
                  )}
                </Box>
              ) : null,
            )}

            {selectedDomain.domainEmails.every(
              (email) => !email.bimiRecord,
            ) && (
              <Typography variant="body2" gutterBottom>
                No BIMI records generated yet. Upload avatars to your emails to
                generate BIMI records.
              </Typography>
            )}
          </>
        ) : selectedDomain.bimiRecord ? (
          <>
            <Typography variant="body2" gutterBottom>
              To display your brand&apos;s logo in email clients, add the
              following BIMI record to your DNS settings:
            </Typography>
            <DnsCopyField
              label="Type"
              value="TXT"
              onCopy={copyToClipboard}
            />
            <DnsCopyField
              label="Name / Host"
              value="default._bimi"
              hint={`Creates default._bimi.${selectedDomain.domain}. Paste only default._bimi if your DNS panel appends the domain.`}
              copyTooltip="Copy host name"
              onCopy={copyToClipboard}
            />
            <DnsCopyField
              label="Value / Content"
              value={selectedDomain.bimiRecord}
              copyTooltip="Copy BIMI record"
              onCopy={copyToClipboard}
            />
          </>
        ) : (
          <Typography variant="body2" gutterBottom>
            No BIMI records generated yet. Upload avatars to your emails to
            generate BIMI records.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <BrandButton onClick={onClose}>Close</BrandButton>
      </DialogActions>
    </Dialog>
  );
};

export default DomainDetailsDialog;
